import React from 'react';
import { Typography, Select, Button } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import SegmentedToggle from '../SegmentedToggle';
import LocalOllamaModelField from './LocalOllamaModelField';
import CatalogChatModelField from './CatalogChatModelField';

const { Text } = Typography;

const SOURCE_OPTIONS = [
  { value: 'cloud', text: 'Cloud' },
  { value: 'local', text: 'Local' },
];

/**
 * Cloud/local LLM model picker (Vault provider + catalog or Ollama).
 * Accepts either a `picker` from useLlmModelPicker or flat props for backward compatibility.
 */
export default function LlmModelPickerSection({
  picker,
  modelSource,
  onModelSourceChange,
  providerId,
  onProviderIdChange,
  model,
  onModelChange,
  cloudProviders = [],
  rewriteCloudModels,
  rewriteCloudModelsLoading,
  cloudModelSearch,
  onCloudModelSearchChange,
  ollamaFamilies,
  ollamaLoading,
  onRegisterOllama,
  navigate,
  disabled,
  modelStepLabel = 'Model',
  showModelStepLabel = true,
  cloudEmptyDescription = 'No chat models for this provider. Try another provider or type a model id.',
  localEmptyDescription = 'No models match. Check that Ollama is running and models are pulled.',
}) {
  const source = picker?.source ?? modelSource;
  const pid = picker?.providerId ?? providerId;
  const modelVal = picker?.model ?? model;
  const setModelVal = picker?.setModel ?? onModelChange;
  const cloudModels = picker?.cloudModels ?? rewriteCloudModels;
  const cloudLoading = picker?.cloudLoading ?? rewriteCloudModelsLoading;
  const cloudSearch = picker?.cloudSearch ?? cloudModelSearch;
  const setCloudSearch = picker?.setCloudSearch ?? onCloudModelSearchChange;
  const families = picker?.ollamaFamilies ?? ollamaFamilies;
  const ollamaLoad = picker?.ollamaLoading ?? ollamaLoading;
  const registerOllama = picker?.registerOllama ?? onRegisterOllama;

  const handleSourceChange = (v) => {
    if (picker) {
      picker.setSource(v);
    } else {
      onModelSourceChange(v);
      onModelChange('');
      onProviderIdChange(null);
    }
  };

  const handleProviderChange = (id) => {
    if (picker) {
      picker.setProviderId(id);
    } else {
      onProviderIdChange(id);
      onModelChange('');
    }
  };

  return (
    <>
      {showModelStepLabel && (
        <Text type="secondary" className="rag-query-rewrite-setting-label">
          {modelStepLabel}
        </Text>
      )}
      <div className="create-collection-visibility-block create-collection-pageindex-source-block">
        <SegmentedToggle
          className="create-collection-visibility-toggle"
          value={source}
          onChange={handleSourceChange}
          options={SOURCE_OPTIONS}
          disabled={disabled}
        />
      </div>
      {source === 'cloud' && (
        <>
          <div className="rag-query-rewrite-setting-label-row">
            <Text type="secondary">Saved cloud provider</Text>
          </div>
          <Select
            placeholder="Select a provider with an API key"
            value={pid}
            onChange={handleProviderChange}
            size="large"
            allowClear
            showSearch
            optionFilterProp="label"
            disabled={disabled}
            options={cloudProviders.map((p) => ({
              value: p.id,
              label: `${p.name} (${p.provider})`,
            }))}
            placement="bottomLeft"
            popupMatchSelectWidth={false}
            dropdownRender={(menu) => (
              <div>
                <div
                  className="create-collection-provider-dropdown-add"
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <Button
                    type="link"
                    block
                    icon={<PlusOutlined />}
                    className="create-collection-provider-add-btn"
                    onClick={() => navigate('/vault?create=1')}
                  >
                    Add new API key in Vault
                  </Button>
                </div>
                {menu}
              </div>
            )}
          />
          {pid && (
            <CatalogChatModelField
              models={cloudModels}
              loading={cloudLoading}
              search={cloudSearch}
              onSearchChange={setCloudSearch}
              value={modelVal}
              onChange={setModelVal}
              emptyDescription={cloudEmptyDescription}
            />
          )}
        </>
      )}
      {source === 'local' && (
        <LocalOllamaModelField
          value={modelVal}
          onChange={setModelVal}
          families={families}
          loading={ollamaLoad}
          emptyDescription={localEmptyDescription}
          onPersistCustom={registerOllama}
        />
      )}
    </>
  );
}
